/**
 * Event block: camera proximity enter/exit on a SceneObject.
 * Checks camera-to-object distance each frame via scene.onBeforeRenderObservable.
 * Fires `enter` when distance drops below threshold, `exit` when it rises above.
 *
 * Config: target (path)
 * Data inputs: distance (threshold in meters, float)
 * Flow outputs: enter, exit
 * Data outputs: distance (current camera distance, float)
 */

import { FlowGraphEventBlock } from "@babylonjs/core/FlowGraph/flowGraphEventBlock";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeNumber } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import type { Observer } from "@babylonjs/core/Misc/observable";
import type { Scene } from "@babylonjs/core/scene";
import { resolveTarget } from './traceContext';

export class TraceProximityEventBlock extends FlowGraphEventBlock {
    private _targetPath: string;
    readonly enter: FlowGraphSignalConnection;
    readonly exit: FlowGraphSignalConnection;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataInput("threshold", RichTypeNumber, 5);
        this.enter = this._registerSignalOutput("enter");
        this.exit = this._registerSignalOutput("exit");
        this.registerDataOutput("distance", RichTypeNumber);
    }

    public override _preparePendingTasks(context: FlowGraphContext): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) return;

        const scene = context.getScene();
        let wasInside = false;
        let cancelled = false;

        const observer = scene.onBeforeRenderObservable.add(() => {
            if (cancelled) return;
            const camera = scene.activeCamera;
            if (!camera) return;

            const camPos = camera.globalPosition;
            const objPos = target.transform.getAbsolutePosition();
            const dist = camPos.subtract(objPos).length();

            this.getDataOutput("distance")?.setValue(dist, context);

            const threshold = this.getDataInput("threshold")!.getValue(context) as number;
            const isInside = dist <= threshold;
            if (isInside && !wasInside) {
                wasInside = true;
                this.enter._activateSignal(context);
            } else if (!isInside && wasInside) {
                wasInside = false;
                this.exit._activateSignal(context);
            }
        });

        context._setExecutionVariable(this, "_observer", observer);
        context._setExecutionVariable(this, "_cancel", { cancel: () => { cancelled = true; } });
    }

    public override _cancelPendingTasks(context: FlowGraphContext): void {
        const cancelHandle = context._getExecutionVariable(this, "_cancel", null) as { cancel: () => void } | null;
        cancelHandle?.cancel();
        const observer = context._getExecutionVariable(this, "_observer", null) as Observer<Scene> | null;
        if (observer) {
            context.getScene().onBeforeRenderObservable.remove(observer);
        }
    }

    public override _executeEvent(_context: FlowGraphContext, _payload: unknown): boolean {
        return true;
    }

    public override getClassName(): string {
        return "TraceProximityEventBlock";
    }
}
